import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { formatMetricValue } from '../../utils/formatMetricValue';
import TrendBadge from './TrendBadge';
import TileMetaActions from '../TileMetaActions';
import { getMetricDisplayName } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import { tileMetaTitlePad } from '../../utils/tileMeta';

/** Design §4 hero: value, trend, description */
export default function HeroRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 0,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'text.secondary',
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            mb: 1.5,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Typography
        variant="h1"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          lineHeight: 1.05,
          fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
        }}
      >
        {displayValue}
      </Typography>
      {data.description ? (
        <Typography
          variant="body1"
          sx={{ color: 'text.primary', mt: 1.5, lineHeight: 1.5, fontWeight: 500 }}
        >
          {data.description}
        </Typography>
      ) : null}
      {data.trend ? (
        <Box sx={{ mt: 1.5 }}>
          <TrendBadge trend={data.trend} size="medium" />
        </Box>
      ) : null}
    </Paper>
  );
}

/** Design §4 heroWithAlert: value, description, trend as pill, badge as alert */
export function HeroWithAlertRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'text.secondary',
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            mb: 1.5,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Typography
        variant="h1"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          lineHeight: 1.05,
          fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
        }}
      >
        {displayValue}
      </Typography>
      {data.badge ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            mt: 1.5,
            color: theme.palette.error.main,
          }}
        >
          <FlagOutlinedIcon sx={{ fontSize: 18, mt: 0.15, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: theme.palette.error.main, lineHeight: 1.5 }}>
            {data.badge.label}
          </Typography>
        </Box>
      ) : null}
      {data.description ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5, lineHeight: 1.5 }}>
          {data.description}
        </Typography>
      ) : null}
      {data.trend ? (
        <Box sx={{ mt: 1.5 }}>
          <TrendBadge trend={data.trend} pill />
        </Box>
      ) : null}
    </Paper>
  );
}
